import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Check, ChevronDown, Plus, Search, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { cn } from "@/lib/utils";
import { supplierKeys } from "@/lib/queryKeys";
import { listSuppliers, type Supplier } from "@/services/supplierService";

const SEARCH_DEBOUNCE_MS = 200;

export interface SupplierPickerProps {
  value: Supplier | null;
  onChange: (supplier: Supplier | null) => void;
  /** Trigger button label when empty. */
  placeholder?: string;
  /** Disable the picker (used while a parent form is submitting). */
  disabled?: boolean;
}

/**
 * Typeahead picker over the tenant's supplier list. Powers the expense
 * form so the user doesn't retype name/RUT for known proveedores.
 * Defers heavy list shrinkage to the backend's existing search param —
 * we send `?search=` and let the API ILIKE on legalName/displayName/rut.
 *
 * Limited to top-50 results in the dropdown to keep the popover fast
 * for tenants with long supplier rosters; users searching for
 * something more specific narrow it via the search box.
 */
export function SupplierPicker({
  value,
  onChange,
  placeholder,
  disabled,
}: SupplierPickerProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const tm = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(tm);
  }, [searchInput, open]);

  useEffect(() => {
    if (!open) {
      setSearchInput("");
      setDebouncedSearch("");
    } else {
      const focusTm = window.setTimeout(() => {
        inputRef.current?.focus();
      }, 60);
      return () => window.clearTimeout(focusTm);
    }
  }, [open]);

  const listParams = useMemo(
    () => ({
      page: 1,
      limit: 50,
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    }),
    [debouncedSearch],
  );

  const { data, isLoading } = useQuery({
    queryKey: supplierKeys.list(listParams),
    queryFn: () => listSuppliers(listParams),
    enabled: open,
    staleTime: 30_000,
  });

  const suppliers = data?.data ?? [];
  const placeholderLabel =
    placeholder ?? t("supplierPicker.placeholder", "Elegir proveedor (opcional)");

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="flex items-center gap-2">
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className={cn(
              "flex-1 justify-between gap-2 font-normal",
              !value && "text-muted-foreground",
            )}
            disabled={disabled}
          >
            <span className="min-w-0 truncate">
              {value
                ? value.displayName?.trim() || value.legalName
                : placeholderLabel}
            </span>
            <ChevronDown
              className="h-4 w-4 shrink-0 opacity-50"
              aria-hidden
            />
          </Button>
        </PopoverTrigger>
        {value ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onChange(null)}
            disabled={disabled}
            aria-label={t("supplierPicker.clear", "Quitar proveedor")}
            className="shrink-0"
          >
            <X className="h-4 w-4" aria-hidden />
          </Button>
        ) : null}
      </div>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="border-b border-border p-2">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              ref={inputRef}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t(
                "supplierPicker.searchPlaceholder",
                "Buscar por nombre o RUT",
              )}
              className="pl-9"
            />
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {isLoading ? (
            <div className="flex justify-center py-6">
              <LoadingLogo size="sm" />
            </div>
          ) : suppliers.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              {t(
                "supplierPicker.noResults",
                "Sin resultados. Probá con otro término o creá un proveedor nuevo.",
              )}
            </div>
          ) : (
            <ul role="listbox" className="space-y-0.5 px-1">
              {suppliers.map((s) => {
                const isSelected = value?.id === s.id;
                return (
                  <li key={s.id} role="option" aria-selected={isSelected}>
                    <button
                      type="button"
                      onClick={() => {
                        onChange(s);
                        setOpen(false);
                      }}
                      className={cn(
                        "flex w-full items-center justify-between gap-3 rounded px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted",
                        isSelected && "bg-muted",
                      )}
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-foreground">
                          {s.displayName?.trim() || s.legalName}
                        </span>
                        {s.rut ? (
                          <span className="block truncate text-xs text-muted-foreground tabular-nums">
                            RUT {s.rut}
                          </span>
                        ) : null}
                      </span>
                      {isSelected ? (
                        <Check
                          className="h-4 w-4 shrink-0 text-primary"
                          aria-hidden
                        />
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="border-t border-border p-1">
          <Button
            asChild
            variant="ghost"
            className="w-full justify-start gap-2 text-sm"
          >
            <Link to="/suppliers/new" target="_blank" rel="noreferrer">
              <Plus className="h-4 w-4" aria-hidden />
              {t("supplierPicker.createNew", "Crear proveedor nuevo")}
            </Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
